import { useMemo, useState, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { assetsClipPublishToLibraryCreate } from '@goldcast/api/content';

import { useNavigate } from 'react-router-dom';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { ClipPublishedStatus } from '@goldcast/player-config/src/types/asset-types';
import ClipRecommendationPlayer from './ClipRecommendationPlayer';
import ClipPreviewTranscript from './ClipPreviewTranscript';
import { CLIP_PREVIEW_TABS, ClipPreviewTab, TAB_CONFIG } from './constants';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import IconButton from '@/components/atoms/Button/IconButton';
import { updateClipRecommendations } from '@/stores/clipRecommendations';
import useDownloadAsset from '@/hooks/useDownloadAsset';
import { useAppContext } from '@/context/AppContext/AppContext';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import useAnalytics from '@/hooks/useAnalytics';
import TooltipV2 from '@/components/TooltipV2';
import Loader from '@/components/atoms/Loader';
import { currentClip } from '@/stores/clip';
import Icon from '@/components/atoms/Icon';

interface ClipPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedClip: Clip | null;
  updateSelectedClip: (clip: Clip) => void;
  isLoading: boolean;
}

const ClipPreviewModal: React.FC<ClipPreviewModalProps> = ({
  isOpen,
  onClose,
  selectedClip,
  updateSelectedClip,
  isLoading
}) => {
  const navigate = useNavigate();
  const { logger } = useAppContext();
  const { trackClipRecommendationEvent } = useAnalytics();

  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState<ClipPreviewTab>(CLIP_PREVIEW_TABS.TRANSCRIPT);
  const totalTimePlayed = useRef(0);
  const allClips = useSyncExternalStore(currentClip.subscribe, currentClip.getSnapshot);
  const clipData = allClips[selectedClip?.id!] || {};

  const timePlayedInterval = useRef<NodeJS.Timeout | null>(null);

  const { onDownloadClip } = useDownloadAsset({
    type: 'CLIP',
    clip: selectedClip as any
  });

  useEffect(() => {
    if (isOpen && selectedClip?.id) {
      trackClipRecommendationEvent('ClipPreviewOpen', { clipId: selectedClip.id });
    }
    totalTimePlayed.current = 0;
  }, [isOpen, selectedClip, trackClipRecommendationEvent]);

  useEffect(() => {
    return () => {
      if (timePlayedInterval.current) {
        clearInterval(timePlayedInterval.current);
      }
    };
  }, []);

  const handleEditClip = useCallback(() => {
    if (!selectedClip?.id) {
      logger.warn('Cannot edit clip: Missing clip ID');
      return;
    }

    trackClipRecommendationEvent('ClipPreviewEdit', { clipId: selectedClip.id });

    const savedSearchId = selectedClip.saved_search?.id;
    if (savedSearchId) {
      try {
        navigate(`/search/${savedSearchId}/clips?activeClipId=${selectedClip.id}`);
      } catch (error) {
        logger.error('Failed to navigate to clip editor:', error);
        showErrorToast('Failed to open clip editor. Please try again.');
      }
    } else {
      logger.warn('Cannot navigate to edit: Missing saved search ID');
      showErrorToast('Cannot edit this clip. Missing saved search information.');
    }
  }, [navigate, selectedClip, trackClipRecommendationEvent, logger]);

  const shouldEnableLayers = useMemo(() => {
    if (!selectedClip?.asset_metadata) {
      return false;
    }

    const clipLayout = selectedClip.asset_metadata.layout;
    const isAudiogram = clipLayout === 'AUDIOGRAM';

    if (isAudiogram) {
      return false;
    }

    const isSpeakerLayout = clipLayout === 'SPEAKER' || clipLayout === 'GRID';
    const showSpeakerLabels = !!selectedClip.asset_metadata.magicLayout?.showSpeakerLabels;

    return (isSpeakerLayout && showSpeakerLabels) || !!selectedClip.asset_metadata.captions?.length;
  }, [selectedClip?.asset_metadata]);

  const disabledAddToLibrary = useMemo(() => {
    if (!selectedClip?.asset_metadata || !clipData?.asset_metadata?.player_config) {
      return true;
    }
    const publishedStatus = selectedClip.asset_metadata.publish_status;
    return (
      publishedStatus === ClipPublishedStatus.PUBLISHED ||
      publishedStatus === ClipPublishedStatus.PROCESSING ||
      !!selectedClip.asset_metadata.video_library_id ||
      isAddingToLibrary
    );
  }, [selectedClip?.asset_metadata, isAddingToLibrary, clipData?.asset_metadata?.player_config]);

  const isPublishFinishedInLibrary = useMemo(() => {
    return !!selectedClip?.asset_metadata?.video_library_id;
  }, [selectedClip?.asset_metadata?.video_library_id]);

  const addToLibrary = useCallback(async () => {
    if (!selectedClip || !selectedClip.id) {
      return;
    }

    // Track the add to library event
    trackClipRecommendationEvent('ClipPreviewAddToLibrary', { clipId: selectedClip.id });

    setIsAddingToLibrary(true);
    assetsClipPublishToLibraryCreate({
      id: selectedClip.id,
      body: {
        enable_layers: shouldEnableLayers
      }
    })
      .then(response => {
        if (response && selectedClip?.id) {
          const clipResponse = response as unknown as Clip;
          const clip: Clip = {
            ...selectedClip,
            asset_metadata:
              selectedClip.asset_metadata && clipResponse.asset_metadata
                ? {
                    ...selectedClip.asset_metadata,
                    publish_status: clipResponse.asset_metadata.publish_status,
                    video_library_id: clipResponse.asset_metadata.video_library_id
                  }
                : selectedClip.asset_metadata
          };
          updateClipRecommendations(selectedClip.id, clip);
          updateSelectedClip(clip);
          showSuccessToast(
            `Video added to library successfully. It's being processed and will be ready in a few minutes.`
          );
        } else if (!response) {
          logger.error('No response from add to library API');
          showErrorToast('Failed to add video to library. Please try again.');
        }
      })
      .catch(error => {
        logger.error('Failed to add clip to library:', error);
        const errorMessage =
          error?.response?.data?.message || error?.message || 'Something went wrong while adding video to library';
        showErrorToast(`${errorMessage}. Please try again later.`);
      })
      .finally(() => {
        setIsAddingToLibrary(false);
      });
  }, [selectedClip, shouldEnableLayers, updateSelectedClip, logger, trackClipRecommendationEvent]);

  const handleDownloadClip = useCallback(() => {
    if (!selectedClip?.id) {
      return;
    }

    trackClipRecommendationEvent('ClipPreviewDownload', { clipId: selectedClip.id });

    setIsDownloading(true);
    onDownloadClip()
      .catch(error => {
        logger.error('Failed to download clip:', error);
        showErrorToast('Failed to download clip. Please try again.');
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }, [onDownloadClip, selectedClip, trackClipRecommendationEvent, logger]);

  const startTimeTracking = useCallback(() => {
    if (timePlayedInterval.current) {
      clearInterval(timePlayedInterval.current);
    }
    timePlayedInterval.current = setInterval(() => {
      totalTimePlayed.current += 1;
    }, 1000);
  }, []);

  const stopTimeTracking = useCallback(() => {
    if (timePlayedInterval.current) {
      clearInterval(timePlayedInterval.current);
      timePlayedInterval.current = null;
    }
  }, []);

  const handlePlay = useCallback(() => {
    if (!selectedClip?.id) {
      return;
    }
    startTimeTracking();
    trackClipRecommendationEvent('ClipPreviewPlayed', { clipId: selectedClip.id });
  }, [selectedClip, trackClipRecommendationEvent, startTimeTracking]);

  const handlePause = useCallback(() => {
    stopTimeTracking();
  }, [stopTimeTracking]);

  const handleClose = useCallback(() => {
    if (timePlayedInterval.current) {
      clearInterval(timePlayedInterval.current);
      timePlayedInterval.current = null;
    }

    if (selectedClip?.id && totalTimePlayed.current > 0) {
      trackClipRecommendationEvent('ClipWatchTime', {
        clipId: selectedClip.id,
        totalTimePlayed: totalTimePlayed.current
      });
      totalTimePlayed.current = 0;
    }

    onClose();
  }, [onClose, selectedClip, trackClipRecommendationEvent]);

  const handleTitleClick = useCallback(() => {
    if (!selectedClip?.content?.project?.id || !selectedClip.content?.id) {
      logger.warn('Cannot navigate: Missing content or project ID');
      return;
    }
    try {
      navigate(`/${selectedClip.content.project.id}/${selectedClip.content.id}/recordings`);
    } catch (error) {
      logger.error('Failed to navigate to recordings:', error);
      showErrorToast('Failed to navigate. Please try again.');
    }
  }, [navigate, selectedClip, logger]);

  const handleTabChange = useCallback(
    (tab: ClipPreviewTab) => {
      setActiveTab(tab);
      if (selectedClip?.id) {
        trackClipRecommendationEvent('ClipPreviewTabChange', {
          clipId: selectedClip.id,
          tab
        });
      }
    },
    [selectedClip, trackClipRecommendationEvent]
  );

  return (
    <ContentDialog
      panelClassNames="max-w-[1200px] w-[1040px]"
      isOpen={isOpen}
      setIsOpen={handleClose}
      title={selectedClip?.title || 'Clip Preview'}
      size="large"
      showDivider={false}
      titleClassName="text-base p-5 py-4"
    >
      {isLoading && (
        <div className="flex min-h-[calc(50vh-56px)] items-center justify-center px-5 pb-5">
          <Loader size="medium" />
        </div>
      )}
      {selectedClip && !isLoading && (
        <div className="flex gap-6 px-5 pb-5">
          {/* Left Side - Video Player */}
          <div className="w-[640px] shrink-0">
            <div className="relative mb-4 aspect-video">
              <ClipRecommendationPlayer
                clip={selectedClip}
                autoPlay={false}
                onPlay={handlePlay}
                onPause={handlePause}
              />
            </div>

            {/* Clip Details */}
            <div>
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 flex-1 gap-1">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <div
                        className="flex max-w-[90%] truncate text-base font-medium text-black"
                        title={selectedClip.title || 'Untitled Clip'}
                      >
                        {selectedClip.title || 'Untitled Clip'}
                      </div>
                      <Icon name="IconSparkles" size={16} className="shrink-0 text-orange-500" />
                    </div>
                    {selectedClip.content && (
                      <p
                        className="flex cursor-pointer items-center gap-1 text-sm text-slate-500 hover:text-orange-500"
                        onClick={handleTitleClick}
                      >
                        <Icon name="IconVideo" size={16} className="shrink-0" />
                        <span className="truncate" title={selectedClip.content.title || 'Untitled Content'}>
                          {selectedClip.content.title || 'Untitled Content'}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                  <TooltipV2
                    tooltipId="edit-clip-tooltip"
                    tooltipElement={
                      <IconButton
                        icon="IconPencil"
                        onClick={handleEditClip}
                        buttonClassName="rounded-full size-8"
                        iconClassName="size-5"
                        trackingId="clip-recommendation-edit"
                      />
                    }
                    tooltipContent="Edit"
                    size="xsmall"
                    colorTheme="dark"
                    place="top"
                    className="z-50 !rounded-md bg-slate-950 px-3 py-2 !text-[10px] font-normal !leading-[15px] text-white"
                  />

                  <TooltipV2
                    tooltipId="download-clip-tooltip"
                    tooltipElement={
                      <IconButton
                        icon={isDownloading ? 'IconLoader2' : 'IconDownload'}
                        onClick={handleDownloadClip}
                        disabled={isDownloading}
                        buttonClassName="rounded-full size-8"
                        iconClassName={`size-5 ${isDownloading ? 'animate-spin' : ''}`}
                        trackingId="clip-recommendation-download"
                      />
                    }
                    tooltipContent="Download"
                    size="xsmall"
                    colorTheme="dark"
                    place="top"
                    className="z-50 !rounded-md bg-slate-950 px-3 py-2 !text-[10px] font-normal !leading-[15px] text-white"
                  />
                  <TooltipV2
                    tooltipId="add-to-library-tooltip"
                    tooltipElement={
                      <IconButton
                        icon={isAddingToLibrary ? 'IconLoader2' : 'IconArrowBarToUp'}
                        onClick={addToLibrary}
                        buttonClassName="rounded-full"
                        iconClassName={`size-4 ${isAddingToLibrary ? 'animate-spin' : ''}`}
                        disabled={disabledAddToLibrary}
                        trackingId="clip-recommendation-add-to-library"
                        content={isAddingToLibrary ? 'Adding...' : 'Add to Library'}
                        variation="filled"
                      />
                    }
                    tooltipContent={
                      isPublishFinishedInLibrary
                        ? 'This clip is saved in your Library. If editing, click Publish to sync your changes.'
                        : ''
                    }
                    size="xsmall"
                    colorTheme="dark"
                    place="top"
                    className="z-50 !rounded-md bg-slate-950 px-3 py-2 !text-[10px] font-normal !leading-[15px] text-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Tabs and Content */}
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="mb-4 flex w-full rounded bg-slate-200">
              {TAB_CONFIG.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex-1 rounded px-4 py-2 text-sm font-medium transition-all ${
                    activeTab === tab.id ? 'm-[2px] bg-white text-black shadow-sm' : 'text-black'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="min-h-0 flex-1">
              {activeTab === CLIP_PREVIEW_TABS.TRANSCRIPT ? (
                <ClipPreviewTranscript clip={selectedClip} onCaptionsGenerated={updateSelectedClip} />
              ) : (
                <>
                  {selectedClip.asset_metadata?.description ? (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <div className="text-sm font-medium text-orange-600">AI Summary</div>
                      <div className="mt-2">
                        <p className="break-words text-sm leading-relaxed text-slate-900">
                          {selectedClip.asset_metadata.description}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-slate-500">
                      <p>No AI summary available for this clip</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </ContentDialog>
  );
};

export default ClipPreviewModal;
